import { Language } from "../engine/strings/language";

const SETTINGS_KEY = "bullettime-settings";

function detectDefaultLanguage(): Language {
	if (typeof navigator === "undefined") return Language.EN;
	const candidates: string[] = [];
	if (Array.isArray(navigator.languages)) {
		for (const lang of navigator.languages) {
			if (typeof lang === "string") candidates.push(lang);
		}
	}
	if (typeof navigator.language === "string") candidates.push(navigator.language);
	for (const lang of candidates) {
		const lower = lang.toLowerCase();
		if (lower.startsWith("pt")) return Language.PT_BR;
		if (lower.startsWith("en")) return Language.EN;
	}
	return Language.EN;
}

export class UserSettings {
	animationSpeedFactor: number;
	soundVolume: number;
	soundEnabled: boolean;
	musicVolume: number;
	musicEnabled: boolean;
	language: Language;
	cameraFollowPlayer: boolean;
	tileset: string;
	telemetryOptOut: boolean;

	constructor(
		animationSpeedFactor: number = 1.0,
		soundVolume: number = 1.0,
		soundEnabled: boolean = true,
		musicVolume: number = 1.0,
		musicEnabled: boolean = true,
		language: Language = detectDefaultLanguage(),
		cameraFollowPlayer: boolean = false,
		tileset: string = "hd",
		telemetryOptOut: boolean = false,
	) {
		this.animationSpeedFactor = animationSpeedFactor;
		this.soundVolume = soundVolume;
		this.soundEnabled = soundEnabled;
		this.musicVolume = musicVolume;
		this.musicEnabled = musicEnabled;
		this.language = language;
		this.cameraFollowPlayer = cameraFollowPlayer;
		this.tileset = tileset;
		this.telemetryOptOut = telemetryOptOut;
	}

	save(): void {
		const json = JSON.stringify({
			animationSpeedFactor: this.animationSpeedFactor,
			soundVolume: this.soundVolume,
			soundEnabled: this.soundEnabled,
			musicVolume: this.musicVolume,
			musicEnabled: this.musicEnabled,
			language: this.language,
			cameraFollowPlayer: this.cameraFollowPlayer,
			tileset: this.tileset,
			telemetryOptOut: this.telemetryOptOut,
		});
		localStorage.setItem(SETTINGS_KEY, json);
	}

	static load(): UserSettings {
		try {
			const raw = localStorage.getItem(SETTINGS_KEY);
			if (raw === null) return new UserSettings();

			const data = JSON.parse(raw);
			const settings = new UserSettings();

			if (typeof data.animationSpeedFactor === "number" && isFinite(data.animationSpeedFactor)) {
				settings.animationSpeedFactor = Math.min(6.0, Math.max(0.1, data.animationSpeedFactor));
			}

			if (typeof data.soundVolume === "number" && isFinite(data.soundVolume)) {
				settings.soundVolume = Math.min(1.0, Math.max(0, data.soundVolume));
			}

			if (typeof data.soundEnabled === "boolean") {
				settings.soundEnabled = data.soundEnabled;
			}

			if (typeof data.musicVolume === "number" && isFinite(data.musicVolume)) {
				settings.musicVolume = Math.min(1.0, Math.max(0, data.musicVolume));
			}

			if (typeof data.musicEnabled === "boolean") {
				settings.musicEnabled = data.musicEnabled;
			}

			if (typeof data.language === "string" && Object.values(Language).includes(data.language as Language)) {
				settings.language = data.language as Language;
			}

			if (typeof data.cameraFollowPlayer === "boolean") {
				settings.cameraFollowPlayer = data.cameraFollowPlayer;
			}

			if (typeof data.tileset === "string" && ["hd"].includes(data.tileset)) {
				settings.tileset = data.tileset;
			}

			if (typeof data.telemetryOptOut === "boolean") {
				settings.telemetryOptOut = data.telemetryOptOut;
			}

			return settings;
		} catch {
			return new UserSettings();
		}
	}

	getEffectiveSoundVolume(): number {
		return this.soundEnabled ? this.soundVolume : 0;
	}

	getEffectiveMusicVolume(): number {
		return this.musicEnabled ? this.musicVolume : 0;
	}
}
